import { problemResponse, registry, undeclaredQueryRefused } from "../registry.js";
import { isoDateTime, z } from "../zod.js";
import { AgentStatus } from "../agents/schemas.js";
import { Uuid } from "../identity/admin-schemas.js";

// Spec 15 §4 S23 and §5, spec 14 §3.10 (D-645): a tenant's agents and the
// model profile each one runs on, read by every staff role and switched by a
// platform_admin. The switch is how an agent moves from `recorded` (replayed
// answers, no model call, no cost) to a Bedrock profile such as `drafting`,
// which spends tokens against the agent's daily budget and the account's
// monthly Bedrock budget. In its own file, like fleet.ts, so the console lanes
// that append to platform/register.ts never collide with it.

/** The adapter an offered profile runs on. `fault` profiles are never offered
 * (spec 14 §3.10: M14-S18's always-failing adapter is a test fixture). */
export const PlatformAgentModelAdapter = z.enum(["bedrock", "recorded"]);

/** What the post-boot probe last said of a profile (spec 14 §3.10, D-539
 * ruling 3). `recorded` never calls the network and is always `ok`;
 * `unchecked` is a Bedrock profile no definite answer has been read for yet. */
export const PlatformAgentModelAvailability = z.enum(["ok", "unavailable", "unchecked"]);

export const PlatformAgentModelProfile = z
  .object({
    /** The profile name `agents.model_profile` stores, never a model id. */
    name: z.string(),
    adapter: PlatformAgentModelAdapter,
    availability: PlatformAgentModelAvailability,
    /** The probe's reason for a definite `unavailable`, else null. */
    unavailable_reason: z.string().nullable(),
    /** Integer micro-dollars per million tokens; null for `recorded`. */
    input_micro_usd_per_million_tokens: z.number().int().min(0).nullable(),
    output_micro_usd_per_million_tokens: z.number().int().min(0).nullable(),
  })
  .openapi("PlatformAgentModelProfile");

export const PlatformAgentModelDeployment = z
  .object({
    /** The profile a newly ensured agent takes; null when none is configured. */
    default_profile: z.string().nullable(),
    /** False when this deployment configures no Bedrock profile, so its task
     * role holds no Bedrock grant (DeployStaging prints `agent-grant: SKIPPED`)
     * and no agent here can draft with a model. */
    bedrock_configured: z.boolean(),
    /** The profiles the console may set, by name. */
    profiles: z.array(PlatformAgentModelProfile),
  })
  .openapi("PlatformAgentModelDeployment");

export const PlatformTenantAgent = z
  .object({
    id: Uuid,
    agent_code: z.string(),
    /** The tenant's own name for the agent; null renders the catalog name. */
    display_name: z.string().nullable(),
    status: AgentStatus,
    model_profile: z.string(),
    daily_token_budget: z.number().int().min(0),
    /** The agents row's optimistic-concurrency version; the switch sends it. */
    version: z.number().int().min(1),
    updated_at: isoDateTime(),
  })
  .openapi("PlatformTenantAgent");

export const PlatformTenantAgentsResponse = z
  .object({
    tenant_id: Uuid,
    deployment: PlatformAgentModelDeployment,
    /** Empty until the agents ensure has reached the tenant (within a minute
     * of it going active on a deployment that configures a profile). */
    items: z.array(PlatformTenantAgent),
  })
  .openapi("PlatformTenantAgentsResponse");

export const PlatformAgentModelProfileSetRequest = z
  .object({
    model_profile: z.string().min(1).max(64),
    /** The version the operator read; a stale one is 409 AGENT_VERSION_CONFLICT. */
    version: z.number().int().min(1),
  })
  .strict()
  .openapi("PlatformAgentModelProfileSetRequest");

const staffSecurity = [{ platformSessionCookie: [] }];
const staffForbidden = (what: string) =>
  problemResponse(`${what}, or TOTP_ENROLLMENT_REQUIRED while the caller's TOTP is unconfirmed`);

registry.registerPath({
  method: "get",
  path: "/v1/platform/tenants/{id}/agents",
  operationId: "platformTenantAgents",
  tags: ["platform"],
  summary: "A tenant's agents and the model profile each runs on (all staff roles)",
  description:
    "Spec 15 §4 S23 (D-645). Each agent with its status, model profile and daily token budget, read from module 14 through its service, and the deployment's profiles the console may set, with their adapter, the probe's last word and their prices. A read, so not fenced and not audited.",
  security: staffSecurity,
  request: { params: z.object({ id: z.guid() }) },
  responses: {
    200: {
      description: "The tenant's agents",
      content: { "application/json": { schema: PlatformTenantAgentsResponse } },
    },
    400: undeclaredQueryRefused,
    401: problemResponse("Not authenticated"),
    403: staffForbidden("Not permitted"),
    404: problemResponse("Unknown tenant, or a host other than the admin host"),
  },
});

registry.registerPath({
  method: "put",
  path: "/v1/platform/tenants/{id}/agents/{agentId}/model-profile",
  operationId: "platformSetAgentModelProfile",
  tags: ["platform"],
  summary: "Switch one of a tenant's agents to another model profile (platform_admin)",
  description:
    "Spec 15 §5, spec 14 §3.10 (D-645). platform_admin only, fenced like every tenant mutation (D-477). The profile must be one this deployment configures and the console offers: a name the deployment does not configure is 422 AGENT_PROFILE_UNKNOWN, whose `reason` is `no_bedrock_profile` when the deployment has no Bedrock profile at all (its task role holds no grant, `agent-grant: SKIPPED`); a `fault` profile is 422 AGENT_PROFILE_NOT_OFFERED; a Bedrock profile the probe has definitely marked unavailable is 409 AGENT_PROFILE_UNAVAILABLE. A stale `version` is 409 AGENT_VERSION_CONFLICT. Setting the profile the agent already runs on changes and audits nothing. A switch writes `agent.updated` on the tenant's chain under the staff member's name and `tenant.agent_model_profile_set` with before and after on the platform chain, and answers the agent as this write left it.",
  security: staffSecurity,
  request: {
    params: z.object({ id: z.guid(), agentId: z.guid() }),
    body: {
      content: { "application/json": { schema: PlatformAgentModelProfileSetRequest } },
    },
  },
  responses: {
    200: {
      description: "The agent as the switch left it",
      content: { "application/json": { schema: PlatformTenantAgent } },
    },
    400: problemResponse("Validation failed, or IDEMPOTENCY_KEY_REQUIRED"),
    401: problemResponse("Not authenticated"),
    403: staffForbidden(
      "Not permitted, or STAFF_SCOPE_DENIED: a sandbox staff member and a tenant that is not sandbox (D-477)",
    ),
    404: problemResponse("Unknown tenant, or AGENT_NOT_FOUND"),
    409: problemResponse(
      "AGENT_VERSION_CONFLICT, AGENT_PROFILE_UNAVAILABLE, IDEMPOTENCY_KEY_REUSE or IDEMPOTENCY_IN_PROGRESS",
    ),
    422: problemResponse(
      "AGENT_PROFILE_UNKNOWN (reason `no_bedrock_profile` or `not_configured`), AGENT_PROFILE_NOT_OFFERED, or VALIDATION_FAILED: the Idempotency-Key is in a namespace the server mints for itself (D-340 ruling 4)",
    ),
  },
});
